import { describeMutationError } from "@api/mutationErrors";
import {
  faArrowUp,
  faCamera,
  faForward,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Band,
  Button,
  Dialog,
  FieldError,
  StatusMark,
} from "@scribenote/brian-design-system";
import type { StaffPhotoPoseGuide } from "@users/staffPhotoClient";
import { useUploadStaffPhoto } from "@users/staffPhotoQueries";
import {
  type RefObject,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";

const COUNTDOWN_SECONDS = 3;
const SHOWN_SAVED_MS = 900;

const ABOVE_HEADING = "Camera above your head";
const ABOVE_DETAIL =
  "Hold your phone up high, lens pointing down, or put the laptop on a shelf above you.";

type Phase =
  | { kind: "countdown"; count: number }
  | { kind: "reposition" }
  | { kind: "saving" | "saved" | "failed"; shot: string };

const COUNTDOWN: Phase = { kind: "countdown", count: COUNTDOWN_SECONDS };

const isTorso = (guide: StaffPhotoPoseGuide | undefined) =>
  guide?.group === "torso";

const isAbove = (guide: StaffPhotoPoseGuide | undefined) =>
  guide?.group === "above";

/** Moving the camera between eye level and overhead takes longer than a countdown, so it waits for Ready. */
const phaseFor = (
  previous: StaffPhotoPoseGuide | undefined,
  next: StaffPhotoPoseGuide | undefined,
): Phase =>
  next &&
  (previous ? previous.group !== next.group : isAbove(next) || isTorso(next))
    ? { kind: "reposition" }
    : COUNTDOWN;

const AboveBanner = () => (
  <div className="overflow-hidden rounded-paper border border-rule bg-card shadow-lift">
    <Band
      tone="flag"
      icon={<FontAwesomeIcon icon={faArrowUp} />}
      className="text-annotation-caps"
    >
      {ABOVE_HEADING}
    </Band>
    <p className="px-3 py-2 text-sm text-ink-body">{ABOVE_DETAIL}</p>
  </div>
);

/** The stream is stopped on close and unmount, so the camera light goes off with the dialog. */
const useWebcam = (
  video: RefObject<HTMLVideoElement | null>,
  active: boolean,
) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!active) return;
    let stream: MediaStream | null = null;
    let closed = false;
    navigator.mediaDevices
      .getUserMedia({ video: { width: { ideal: 1280 } }, audio: false })
      .then((opened) => {
        stream = opened;
        if (closed) opened.getTracks().forEach((track) => track.stop());
        else if (video.current) video.current.srcObject = opened;
      })
      .catch(() => setFailed(true));
    return () => {
      closed = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [video, active]);
  return failed;
};

/** The canvas draws the camera's own frame, so the upload is not mirrored like the preview. */
const grabFrame = (source: HTMLVideoElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = source.videoWidth;
    canvas.height = source.videoHeight;
    canvas.getContext("2d")?.drawImage(source, 0, 0);
    canvas.toBlob(resolve, "image/jpeg", 0.9);
  });

const FaceOval = () => (
  <svg
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 h-full w-full"
  >
    <defs>
      <mask id="staff-photo-face-oval">
        <rect width="100" height="100" fill="white" />
        <ellipse cx="50" cy="47" rx="17" ry="38" fill="black" />
      </mask>
    </defs>
    <rect
      width="100"
      height="100"
      mask="url(#staff-photo-face-oval)"
      className="fill-nav opacity-50"
    />
    <ellipse
      cx="50"
      cy="47"
      rx="17"
      ry="38"
      fill="none"
      strokeWidth={2}
      strokeDasharray="6 4"
      vectorEffect="non-scaling-stroke"
      className="stroke-paper"
    />
  </svg>
);

const TorsoFrame = ({ aspect }: { aspect: number }) => {
  const height = Math.min(0.9, (0.9 * aspect) / (3 / 4));
  const width = (height * (3 / 4)) / aspect;
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      <rect
        x={50 * (1 - width)}
        y={50 * (1 - height)}
        width={100 * width}
        height={100 * height}
        rx={2}
        fill="none"
        strokeWidth={2}
        strokeDasharray="6 4"
        vectorEffect="non-scaling-stroke"
        className="stroke-paper"
      />
    </svg>
  );
};

/**
 * One webcam session through the given poses in order: a countdown shot per
 * pose, each uploaded before the next starts so uploads never race.
 */
export const GuidedPhotoCapture = ({
  poses,
  consentVersion,
  onClose,
}: {
  /** Fixed for the session; the page's list shifts as each upload lands. */
  poses: StaffPhotoPoseGuide[];
  consentVersion: string;
  onClose: () => void;
}) => {
  const video = useRef<HTMLVideoElement>(null);
  const taking = useRef(false);
  const upload = useUploadStaffPhoto({ handled: true });
  const [ready, setReady] = useState(false);
  const [frameAspect, setFrameAspect] = useState(16 / 9);
  const [index, setIndex] = useState(0);
  const [saved, setSaved] = useState(0);
  const [phase, setPhase] = useState<Phase>(() =>
    phaseFor(undefined, poses[0]),
  );
  const guide = poses.at(index);
  const done = !guide;
  const failed = useWebcam(video, !done);
  const shot = "shot" in phase ? phase.shot : null;

  useEffect(() => (shot ? () => URL.revokeObjectURL(shot) : undefined), [shot]);

  const advance = () => {
    setIndex(index + 1);
    setPhase(phaseFor(poses[index], poses[index + 1]));
  };

  const shoot = async () => {
    const source = video.current;
    if (!guide || !source || source.videoWidth === 0 || taking.current) return;
    taking.current = true;
    const blob = await grabFrame(source);
    taking.current = false;
    if (!blob) {
      setPhase(COUNTDOWN);
      return;
    }
    const taken = URL.createObjectURL(blob);
    setPhase({ kind: "saving", shot: taken });
    upload.mutate(
      { file: blob, consentVersion, pose: guide.pose, guided: true },
      {
        onSuccess: () => {
          setSaved((count) => count + 1);
          setPhase({ kind: "saved", shot: taken });
        },
        onError: () => setPhase({ kind: "failed", shot: taken }),
      },
    );
  };

  const tick = useEffectEvent(() => {
    if (phase.kind === "saved") advance();
    else if (phase.kind === "countdown" && phase.count > 1)
      setPhase({ kind: "countdown", count: phase.count - 1 });
    else if (phase.kind === "countdown") void shoot();
  });

  useEffect(() => {
    if (done || !ready) return;
    if (phase.kind !== "countdown" && phase.kind !== "saved") return;
    const timer = setTimeout(
      tick,
      phase.kind === "saved" ? SHOWN_SAVED_MS : 1000,
    );
    return () => clearTimeout(timer);
  }, [phase, ready, done]);

  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      title="Reference photos"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {!done && (
            <>
              <Button
                variant="ghost"
                disabled={phase.kind === "saving"}
                onClick={advance}
              >
                <FontAwesomeIcon icon={faForward} aria-hidden="true" />
                Skip
              </Button>
              {phase.kind === "failed" ? (
                <Button variant="accent" onClick={() => setPhase(COUNTDOWN)}>
                  <FontAwesomeIcon icon={faCamera} aria-hidden="true" />
                  Try again
                </Button>
              ) : (
                <Button
                  variant="accent"
                  disabled={failed || !ready || phase.kind !== "countdown"}
                  onClick={() => void shoot()}
                >
                  <FontAwesomeIcon icon={faCamera} aria-hidden="true" />
                  Take now
                </Button>
              )}
            </>
          )}
          <Button variant={done ? "accent" : "outline"} onClick={onClose}>
            {done ? "Done" : "Close"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {done ? (
          <p className="text-sm text-ink-body">
            Saved {saved} of {poses.length}{" "}
            {poses.length === 1 ? "photo" : "photos"}.
          </p>
        ) : (
          <>
            <StatusMark>
              {`Photo ${index + 1} of ${poses.length} · ${guide.label}`}
            </StatusMark>
            {isAbove(guide) && <AboveBanner />}
            <p className="font-display text-2xl font-medium text-ink">
              {guide.instruction}
            </p>
          </>
        )}
        {failed && !done ? (
          <FieldError>I couldn’t open your camera.</FieldError>
        ) : (
          <div
            data-dd-privacy="hidden"
            className={
              done
                ? "hidden"
                : "relative w-full overflow-hidden rounded-paper border border-rule bg-recess"
            }
            style={{ aspectRatio: frameAspect }}
          >
            <video
              ref={video}
              autoPlay
              playsInline
              muted
              onLoadedMetadata={(event) => {
                const source = event.currentTarget;
                if (source.videoWidth > 0 && source.videoHeight > 0)
                  setFrameAspect(source.videoWidth / source.videoHeight);
              }}
              onPlaying={() => setReady(true)}
              className="h-full w-full -scale-x-100 object-contain"
            />
            {isTorso(guide) ? (
              <TorsoFrame aspect={frameAspect} />
            ) : (
              <FaceOval />
            )}
            {phase.kind === "countdown" && ready && (
              <span
                aria-live="polite"
                className="absolute inset-0 flex items-center justify-center font-display text-7xl font-medium text-paper"
              >
                {phase.count}
              </span>
            )}
            {phase.kind === "reposition" && (
              <div
                role="status"
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-nav/70 p-4 text-center"
              >
                <p className="font-display text-2xl font-medium text-paper">
                  {isTorso(guide)
                    ? "Step back for your torso photos"
                    : isAbove(guide)
                      ? ABOVE_HEADING
                      : "Camera back to eye level"}
                </p>
                {isTorso(guide) && (
                  <p className="max-w-sm text-sm text-paper">
                    Keep your whole head and torso down to your hips inside the
                    frame. Press Ready when you are in position.
                  </p>
                )}
                {isAbove(guide) && (
                  <p className="max-w-sm text-sm text-paper">{ABOVE_DETAIL}</p>
                )}
                <Button variant="accent" onClick={() => setPhase(COUNTDOWN)}>
                  Ready
                </Button>
              </div>
            )}
            {shot && (
              <img
                src={shot}
                alt="The photo just taken"
                referrerPolicy="no-referrer"
                data-dd-privacy="hidden"
                className="absolute inset-0 h-full w-full bg-recess object-cover"
              />
            )}
          </div>
        )}
        {phase.kind === "saving" && (
          <p className="text-sm text-annotation">Saving…</p>
        )}
        {phase.kind === "saved" && (
          <p className="text-sm text-ink-body">Saved.</p>
        )}
        {phase.kind === "failed" && (
          <FieldError>
            {describeMutationError(upload.error, "save the photo")}
          </FieldError>
        )}
      </div>
    </Dialog>
  );
};
